import React from 'react';
import { Badge, Card, Row, Col } from 'react-bootstrap';

import {
  GetExamTypeDto,
  GetPaymentDto,
  GetPaymentDtoStatusEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { add, subtract, max } from '../../../utils/mathUtils';

interface VisitPaymentStatusProps {
  payments?: GetPaymentDto[];
  examType?: GetExamTypeDto;
}

interface PaymentSummary {
  paidAmount: number;
  examPrice: number;
  isFullyPaid: boolean;
  isPartiallyPaid: boolean;
  remainingAmount: number;
  returnedAmount: number;
}

const calculatePaymentSummary = (
  payments: GetPaymentDto[],
  examType: GetExamTypeDto | undefined,
): PaymentSummary => {
  let paidAmount = 0;
  let returnedAmount = 0;

  payments.forEach(payment => {
    if (payment.status === GetPaymentDtoStatusEnum.Success) {
      paidAmount = add(paidAmount, Number(payment.amount || 0));
    }
    if (payment.status === GetPaymentDtoStatusEnum.Returned) {
      returnedAmount = add(returnedAmount, Number(payment.amount || 0));
      paidAmount = subtract(paidAmount, Number(payment.amount || 0));
    }
  });

  const examPrice = examType?.price ? Number(examType.price) : 0;
  const isFullyPaid = examPrice > 0 && paidAmount >= examPrice;
  const isPartiallyPaid =
    examPrice > 0 && paidAmount > 0 && paidAmount < examPrice;
  const remainingAmount = max(0, subtract(examPrice, paidAmount));

  return {
    paidAmount,
    examPrice,
    isFullyPaid,
    isPartiallyPaid,
    remainingAmount,
    returnedAmount,
  };
};

const PaymentSummarySection: React.FC<{
  summary: PaymentSummary;
  currency: string;
}> = ({ summary, currency }) => {
  if (summary.examPrice === 0) {
    return null;
  }

  const badgeVariant = summary.isFullyPaid
    ? 'success'
    : summary.isPartiallyPaid
      ? 'warning'
      : 'danger';

  const statusText = summary.isFullyPaid
    ? '✓ Opłacone w całości'
    : summary.isPartiallyPaid
      ? '⚠ Częściowo opłacone'
      : '○ Nieopłacone';

  return (
    <Card className="mb-3" border={badgeVariant}>
      <Card.Body>
        <Row className="mb-3">
          <Col>
            <Card.Title className="mb-0">Podsumowanie płatności</Card.Title>
          </Col>
          <Col className="text-end">
            <Badge bg={badgeVariant}>{statusText}</Badge>
          </Col>
        </Row>

        <Row>
          <Col md={3}>
            <div className="text-muted small mb-1">Cena badania</div>
            <div className="fw-bold fs-5">
              {summary.examPrice.toFixed(2)} {currency}
            </div>
          </Col>
          <Col md={3}>
            <div className="text-muted small mb-1">Zapłacono</div>
            <div className="fw-bold fs-5">
              {summary.paidAmount.toFixed(2)} {currency}
            </div>
          </Col>
          {summary.returnedAmount > 0 && (
            <Col md={3}>
              <div className="text-muted small mb-1">Zwrócono</div>
              <div className="fw-bold fs-5 text-info">
                {summary.returnedAmount.toFixed(2)} {currency}
              </div>
            </Col>
          )}
          {summary.isPartiallyPaid && (
            <Col md={3}>
              <div className="text-muted small mb-1">Do zapłaty</div>
              <div className="fw-bold fs-5 text-danger">
                {summary.remainingAmount.toFixed(2)} {currency}
              </div>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export const VisitPaymentStatus: React.FC<VisitPaymentStatusProps> = ({
  payments,
  examType,
}) => {
  if (!payments || payments.length === 0) {
    return null;
  }

  const summary = calculatePaymentSummary(payments, examType);
  const currency = payments[0]?.currency || '';

  return <PaymentSummarySection summary={summary} currency={currency} />;
};
